/**
 * Legacy links into the 2nd Operation family, translated to this report's parameters.
 *
 * The legacy Summary and Detail screens pointed here with different parameter vocabularies, and
 * the Summary screen's per-row "view" link carried eight of them at once. The translation runs
 * once on arrival and replaces the history entry, so Back does not bounce through the old address.
 *
 * What cannot be honoured is named rather than dropped in silence — `unsupported` goes on screen.
 */

/** Legacy parameter names that identify an inbound link. `path` is the legacy view id. */
const LEGACY_PARAMS = [
  'path',
  'from_department',
  'to_department',
  'dept_code',
  'from_dept',
  'startDate',
  'endDate',
  'optime_start',
  'optime_end',
  'userCode',
  'plus_order_count',
  'bundle_desc',
] as const;

/** Legacy view id → the view this report opens on. */
const VIEW_BY_PATH: Record<string, string> = {
  'sftsRpt-2nd-2ndOperationSummaryReport_v2': 'summary',
  'sftsRpt-2nd-2ndOperationDetailReport_v2': 'detail',
  // The miMes variants are not ported. Their readers land on the equivalent view of the ported
  // report, which reads the same fact without the MI scope — so the notice says so plainly.
  'sftsRpt-miMes-2nd-2ndOperationSummaryReport': 'summary',
  'sftsRpt-miMes-2nd-2ndOperationDetailReport': 'detail',
};

const MI_PATHS = new Set([
  'sftsRpt-miMes-2nd-2ndOperationSummaryReport',
  'sftsRpt-miMes-2nd-2ndOperationDetailReport',
]);

/** Legacy name → this report's name, where only the spelling changed. */
const RENAMED: Record<string, string> = {
  from_department: 'from_dept',
  to_department: 'to_dept',
  dept_code: 'to_dept',
  from_dept: 'from_dept',
  optime_start: 'from',
  optime_end: 'to',
  startDate: 'from',
  endDate: 'to',
  userCode: 'user_code',
};

/**
 * Legacy parameters this report has no home for. `plus_order_count` and `bundle_desc` existed only
 * on the miMes screens, which are not ported.
 */
const UNSUPPORTED: Record<string, string> = {
  plus_order_count: 'the plus-order-count filter',
  bundle_desc: 'the part-code filter',
};

export function hasLegacyParams(params: URLSearchParams): boolean {
  return LEGACY_PARAMS.some((name) => params.has(name));
}

/**
 * Legacy sent `ALL` for an unfiltered department and an empty string for an absent value. Here an
 * absent filter is absent: both become an omitted parameter, because `ALL` is a rendering of
 * "no department chosen", not a department.
 */
function isAbsent(value: string): boolean {
  return value === '' || value === 'ALL';
}

/** Legacy appended a time of day to the date it put in the URL; this report takes dates. */
function toDate(value: string): string {
  return value.slice(0, 10);
}

export function translateDeepLink(params: URLSearchParams): {
  params: URLSearchParams;
  unsupported: string | null;
} {
  const next = new URLSearchParams(params);
  const notices: string[] = [];

  const path = params.get('path');
  if (path !== null) {
    next.delete('path');
    const view = VIEW_BY_PATH[path];
    if (view !== undefined) next.set('view', view);
    else notices.push('this legacy report is not available here');
    if (MI_PATHS.has(path)) {
      notices.push('the MI MES variant is not ported yet, so this shows the standard report');
    }
  }

  for (const [legacy, current] of Object.entries(RENAMED)) {
    const value = params.get(legacy);
    if (value === null) continue;
    next.delete(legacy);
    if (isAbsent(value)) continue;
    next.set(current, current === 'from' || current === 'to' ? toDate(value) : value);
  }

  for (const [legacy, description] of Object.entries(UNSUPPORTED)) {
    if (!params.has(legacy)) continue;
    next.delete(legacy);
    notices.push(`${description} did not carry over`);
  }

  // The remaining drill-through keys keep their names; only their empty/ALL sentinels change.
  for (const name of ['b_no', 'batch_no', 'set_group', 'size', 'partnos', 'pro_group']) {
    const value = params.get(name);
    if (value !== null && isAbsent(value)) next.delete(name);
  }

  return {
    params: next,
    unsupported:
      notices.length === 0
        ? null
        : `Opened from a Win Master link — ${notices.join(', ')}.`,
  };
}
